import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "How does duplicate detection work?",
    answer: "Pattern matching across vendor names, amounts, dates, and invoice numbers. Fuzzy logic catches near-duplicates with slight variations. 95%+ accuracy. Zero configuration required."
  },
  {
    question: "What does reconciliation actually do?",
    answer: "Matches bank transactions to your accounting entries automatically. Finds discrepancies before they become problems. Days of manual work compressed into under 2 minutes."
  },
  {
    question: "What's the pricing structure?",
    answer: "Free tier: 100 transactions/month. Pro: ₹2,999/month for unlimited transactions and advanced features. Enterprise: custom pricing with dedicated support and integrations."
  },
  {
    question: "How is my data protected?",
    answer: "AES-256 encryption. Row-level security isolation. Regular security audits. Your data stays yours—never shared, never sold. Bank-grade protection as standard."
  },
  {
    question: "Which systems integrate out of the box?",
    answer: "Tally ERP via XML. CSV bank statements with auto-mapping. PDF/image invoice uploads with OCR extraction. REST API for custom integrations."
  },
  {
    question: "How accurate is invoice data extraction?",
    answer: "98%+ accuracy on vendor names, amounts, dates, and invoice numbers. Continuous learning from corrections. Uncertain items flagged for human review—nothing slips through."
  },
  {
    question: "What file formats are supported?",
    answer: "CSV for bank statements. XML for Tally ledgers. PDF and images for invoices. API endpoints for programmatic data feeds."
  },
  {
    question: "How fast is the matching engine?",
    answer: "Thousands of transactions processed in seconds. Full reconciliation in under 2 minutes. One-click approval for suggested matches. Real-time results, not batch processing."
  },
  {
    question: "Does it work offline?",
    answer: "Progressive Web App architecture. View cached data offline. Actions sync automatically when connection restores. Core analysis requires connectivity."
  },
  {
    question: "What reports can I generate?",
    answer: "Reconciliation summaries. Duplicate payment analysis. Vendor risk assessments. Cash flow forecasts. Full audit trails. Export to PDF or CSV. Schedule automated delivery."
  }
];

export const FAQSection = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background to-surface/50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/15 border border-primary/20 mb-6">
            <HelpCircle className="w-4 h-4 text-primary-dark dark:text-primary" />
            <span className="text-sm font-medium text-primary-dark dark:text-primary">Got Questions?</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-primary bg-clip-text text-transparent pb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Everything you need to know about Sentinel
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="border border-border/50 rounded-3xl px-6 bg-card/50 backdrop-blur-sm data-[state=open]:bg-card data-[state=open]:border-primary/30 data-[state=open]:shadow-xl data-[state=open]:shadow-primary/5 transition-all duration-300"
            >
              <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline py-6 [&[data-state=open]]:text-primary-dark dark:[&[data-state=open]]:text-primary">
                <span className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-dark text-white flex items-center justify-center text-sm font-bold">
                    {(index + 1).toString().padStart(2, '0')}
                  </span>
                  {faq.question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-6 pl-12">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
